import React from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown } from 'antd';
import { DownloadOutlined, DownOutlined, LinkOutlined, LoadingOutlined, ShareAltOutlined } from '@ant-design/icons';
import globalNotification from '../../common/hooks/globalNotification';
import { notificationType } from '../../common/constants/notificationType';
import { ShareMenuItem } from './ShareMenuItem';

/**
 * One Share button: a menu with "Copy shared link" (the report's public /shared-report/<link>) and "Download PDF"
 * (the browser's save-as-PDF of this page). Without a link to share it is a plain "Download PDF" button. `waiting`:
 * a download was asked for and waits for the page to finish loading, so the button shows a spinner meanwhile.
 */
export const ShareButton = ({ sharedLink, onDownload, waiting = false }) => {
    const { t } = useTranslation(['reportPage', 'carReport']);

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(`${window.location.origin}/shared-report/${sharedLink}`);
            globalNotification.open({
                type: notificationType.SUCCESS,
                message: t('carReport:shareReport.success'),
                description: t('carReport:shareReport.linkCopied'),
            });
        } catch (error) {
            globalNotification.open({
                type: notificationType.ERROR,
                message: t('carReport:shareReport.error'),
                description: t('carReport:shareReport.copyFailed'),
            });
        }
    };

    if (!sharedLink) {
        return (
            <button type="button" className="tb-btn primary" onClick={onDownload} aria-busy={waiting}>
                {waiting ? <LoadingOutlined aria-hidden="true" /> : <DownloadOutlined aria-hidden="true" />}
                {t('reportPage:toolbar.downloadPdf')}
            </button>
        );
    }

    const items = [
        {
            key: 'link',
            label: (
                <ShareMenuItem
                    icon={<LinkOutlined />}
                    title={t('reportPage:toolbar.copyLink')}
                    sub={t('reportPage:toolbar.copyLinkSub')}
                />
            ),
        },
        {
            key: 'pdf',
            label: (
                <ShareMenuItem
                    icon={<DownloadOutlined />}
                    title={t('reportPage:toolbar.downloadPdf')}
                    sub={t('reportPage:toolbar.downloadPdfSub')}
                />
            ),
        },
    ];
    const onClick = ({ key }) => (key === 'link' ? copyLink() : onDownload());

    return (
        <Dropdown menu={{ items, onClick }} trigger={['click']} placement="bottomRight" overlayClassName="share-menu">
            <button type="button" className="tb-btn primary" aria-haspopup="menu" aria-busy={waiting}>
                {waiting ? <LoadingOutlined aria-hidden="true" /> : <ShareAltOutlined aria-hidden="true" />}
                {t('reportPage:toolbar.share')}
                <DownOutlined aria-hidden="true" className="tb-caret" />
            </button>
        </Dropdown>
    );
};
